import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { OpenAPI } from "pwapi/core/OpenAPI";

import { SysState, selectFeatureFlag } from "../reducers/sysReducer";

const {
  REACT_APP_WS_HOST,
  REACT_APP_WS_PORT,
  REACT_APP_WS_PROTOCOL,
  NODE_ENV,
  REACT_APP_FORCE_API_PROXY,
} = process.env;

// Changes to this condition should also be applied to utils/configureRemoteAPI.ts
const useProxy = REACT_APP_FORCE_API_PROXY === "true";

const webSocketBaseUrl =
  NODE_ENV === "test"
    ? "ws://localhost:8080/ws"
    : useProxy
    ? "ws://localhost:3000/wss"
    : `${REACT_APP_WS_PROTOCOL}://${REACT_APP_WS_HOST}:${REACT_APP_WS_PORT}/ws`;

const useWebSocket = (
  address: string | false | null,
  instantConnect?: boolean,
  options?: {
    onOpen?: EventListener;
    onClose?: EventListener;
    onMessage?: EventListener;
    onMessageData?: (data: any) => void;
  }
) => {
  const webSocketDebugging = useSelector((state: { sys: SysState }) =>
    selectFeatureFlag(state, "webSocketDebugging")
  );
  const socketRef = useRef<WebSocket & { reconnect?: Function }>();

  const connect = (message?: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) return;

    const socket = new WebSocket(
      `${webSocketBaseUrl}/${address}/?token=${OpenAPI.TOKEN}`
    );

    socket.onopen = function (e: any) {
      if (webSocketDebugging) console.log(`WebSocket opened for ${address}`, e);

      if (options?.onOpen) options.onOpen(e);
      if (message) socket.send(message);
    };

    socket.onmessage = function (e) {
      if (webSocketDebugging)
        console.log(`WebSocket message received from ${address}`, e);

      if (options?.onMessage) options.onMessage(e);

      if (options?.onMessageData) {
        try {
          options.onMessageData(JSON.parse(e.data));
        } catch (e) {
          if (webSocketDebugging) console.warn(e);
        }
      }
    };

    socket.onclose = function (e: any) {
      if (webSocketDebugging) {
        if (!e.isTrusted || !e.wasClean) {
          console.warn(`WebSocket closed unexpectedly for ${address}`, e);
        } else {
          console.log(`WebSocket closed for ${address}`, e);
        }
      }

      if (options?.onClose) options.onClose(e);
    };

    socketRef.current = socket;
    socketRef.current.reconnect = (message?: string) => {
      socket.close();
      connect(message);
    };
  };

  useEffect(() => {
    if (instantConnect) {
      if (address && !socketRef.current) connect();

      if (!address && socketRef.current) {
        if (webSocketDebugging) console.log("WebSocket closing");
        socketRef.current.close();
        socketRef.current = undefined;
      }
    }
    return () => {
      if (socketRef.current) {
        if (webSocketDebugging) console.log("WebSocket closing");
        socketRef.current.close();
        socketRef.current = undefined;
      }
    };
  }, [address]);

  return {
    ref: socketRef,
    connect,
  };
};

export default useWebSocket;
